import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { FiHeart, FiShield, FiX } from "react-icons/fi";
import "../../assets/ThankYouPage.css";
import { useTranslation } from "react-i18next";
import SiteHeader from "../SiteHeader";

// Minutes the participant is asked to keep WhatsApp open after donating (the
// server front-loads the sweep in this window).
const KEEP_OPEN_MS = 10 * 60 * 1000;

const ThankYouPage = () => {
    const exited = Boolean((useLocation().state || {}).exit);
    // window.close() only works for tabs a script opened; browsers ignore it for
    // a tab the participant opened themselves (2026-10-26: "the page does not
    // close"). Try it, and when the tab is still here a moment later, say so.
    const [closeRefused, setCloseRefused] = useState(false);
    // 10-minute "keep WhatsApp open" countdown (2026-10-26): the server front-loads
    // the sweep in this window. Anchored in sessionStorage so a refresh does not
    // restart it; a background tab still counts down (wall clock, not ticks).
    const [remainingMs, setRemainingMs] = useState(KEEP_OPEN_MS);
    useEffect(() => {
        if (exited) return undefined;
        let startedAt;
        try {
            startedAt = Number(sessionStorage.getItem("ty_keep_open_started_at"));
            if (!startedAt) { startedAt = Date.now(); sessionStorage.setItem("ty_keep_open_started_at", String(startedAt)); }
        } catch (e) { startedAt = Date.now(); }
        const tick = () => setRemainingMs(Math.max(0, KEEP_OPEN_MS - (Date.now() - startedAt)));
        tick();
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, [exited]);
    const mm = String(Math.floor(remainingMs / 60000)).padStart(2, "0");
    const ss = String(Math.floor((remainingMs % 60000) / 1000)).padStart(2, "0");
    const handleClose = () => {
        try { window.close(); } catch (e) { /* ignore */ }
        setTimeout(() => { if (!window.closed) setCloseRefused(true); }, 400);
    };
    const { t } = useTranslation();
    // Donation is complete and final — block back-navigation so the donor
    // can't return to the Dashboard (or earlier steps) from here. Dashboard
    // already navigates here with { replace: true }, which removes itself
    // from history; this additionally traps back/forward gestures that
    // would otherwise walk further back (e.g. to chat-selections).
    useEffect(() => {
        window.history.pushState(null, "", window.location.href);
        const trapBack = () => {
            window.history.pushState(null, "", window.location.href);
        };
        window.addEventListener("popstate", trapBack);
        return () => window.removeEventListener("popstate", trapBack);
    }, []);

    return (
        <div className="ty-app">
            <SiteHeader />

            <main className="ty-main">
                <div className="ty-badge">
                    <FiHeart />
                </div>

                {exited ? (
                    <>
                        <h2>{t("thankyou.exitTitle")}</h2>
                        <p>{t("thankyou.exitBody")}</p>
                    </>
                ) : (
                    <>
                        <h2>{t("thankyou.done")}</h2>
                        <p>
                            {t("thankyou.received")}
                        </p>
                        {/* Two-sweep design (2026-09-26): links/text are saved before this
                        page shows; any remaining shared images/videos continue
                        syncing on our side in the background. Honest about the one
                        real dependency (the phone must be on with WhatsApp still
                        linked) — wording pending PI approval alongside the
                        session-retention policy. */}
                        <p className="ty-background-note">
                            {t("thankyou.background")}
                        </p>
                        {remainingMs > 0 ? (
                            <div className="ty-timer" role="timer" aria-live="off">
                                <p className="ty-background-note"><strong>{t("thankyou.keepOpen")}</strong></p>
                                <div className="ty-timer__label">{t("thankyou.timerLabel")}</div>
                                <div className="ty-timer__clock" aria-label={`${mm}:${ss}`}>{mm}:{ss}</div>
                                <div className="ty-timer__hint">{t("thankyou.timerHint")}</div>
                            </div>
                        ) : (
                            <p className="ty-background-note ty-timer__done" role="status"><strong>{t("thankyou.timerDone")}</strong></p>
                        )}
                        <p>{t("thankyou.unlink")}</p>
                        <p>{t("thankyou.notifications")}</p>
    
                        <div className="ty-divider" />
    
                        <div className="ty-reassure">
                            <FiShield />
                            {t("thankyou.nothingElse")}
                        </div>
                    </>
                )}

                <button
                    className="ty-close-btn"
                    onClick={handleClose}
                >
                    <FiX />
                    {t("common.close")}
                </button>
                {closeRefused && <p className="ty-background-note" role="status">{t("thankyou.closeFallback")}</p>}
                <p className="ty-close-note">
                    {t("common.closeNote")}
                </p>
            </main>

            <footer className="ty-footer">
                <div className="ty-footer-logo">GU</div>
                <p>{t("common.footer", { year: new Date().getFullYear() })}</p>
            </footer>
        </div>
    );
};

export default ThankYouPage;
